/**
 * GovernmentDashboardPage — `/government` index landing.
 *
 * Live KPI tiles backed by `GET /v1/government/stats/dashboard` (revenue
 * today/month, requests today/month, in-flight, avg processing hours).
 * Recent service-requests table backs onto `GET /v1/government/requests`.
 */
import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowRightLeft, Clock, DollarSign, Landmark } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import {
  getDashboardStats,
  listGovernmentServices,
  listServiceRequests,
} from '../../lib/api/government';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';

function formatNgn(amount: string | undefined, currency = 'NGN'): string {
  if (!amount) return '—';
  const n = Number.parseFloat(amount);
  if (!Number.isFinite(n)) return amount;
  return `${currency} ${n.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function GovernmentDashboardPage(): JSX.Element {
  const services = useQuery({
    queryKey: ['gov', 'services'],
    queryFn: () => listGovernmentServices({ activeOnly: true, pageSize: 50 }),
    retry: false,
    staleTime: 30_000,
  });
  const requests = useQuery({
    queryKey: ['gov', 'requests'],
    queryFn: () => listServiceRequests({ pageSize: 25 }),
    retry: false,
    staleTime: 30_000,
  });
  const stats = useQuery({
    queryKey: ['gov', 'stats', 'dashboard'],
    queryFn: getDashboardStats,
    retry: false,
    staleTime: 15_000,
  });

  const requestsDenied =
    requests.isError &&
    requests.error instanceof ApiError &&
    (requests.error.status === 401 || requests.error.status === 403);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Government services"
        description="MDA integration + citizen service delivery."
        actions={
          <div className="flex items-center gap-2">
            <Link
              to="/government/intake"
              className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark inline-flex items-center gap-2"
            >
              Process service <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/government/agencies"
              className="px-3 py-2 border border-border rounded-lg text-sm font-medium hover:bg-gray-50"
            >
              Agency status
            </Link>
          </div>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Active services"
          value={services.isPending ? '—' : String(services.data?.total ?? 0)}
          hint="Currently active government services"
          icon={Landmark}
        />
        <KPICard
          title="Recent requests"
          value={requests.isPending ? '—' : String(requests.data?.total ?? 0)}
          hint="Recent citizen requests"
          icon={ArrowRightLeft}
        />
        <KPICard
          title="Revenue today"
          value={stats.isPending ? '—' : formatNgn(stats.data?.revenueToday, stats.data?.currency)}
          hint={stats.isError ? 'Stats unavailable' : `${stats.data?.requestsToday ?? 0} completed today`}
          icon={DollarSign}
          tone={stats.isError ? 'warning' : 'success'}
        />
        <KPICard
          title="Avg processing"
          value={stats.isPending ? '—' : `${stats.data?.avgProcessingHours ?? 0} h`}
          hint={stats.isError ? 'Stats unavailable' : `${stats.data?.inFlight ?? 0} in flight`}
          icon={Clock}
          tone={stats.isError ? 'warning' : undefined}
        />
      </div>

      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Recent service requests</h2>
        {requests.isPending ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : requestsDenied ? (
          <DeferredNotice title="Permission required">
            The dev token doesn&apos;t carry <code className="font-mono">Government/readRequest</code>.
          </DeferredNotice>
        ) : requests.isError ? (
          <DeferredNotice title="Service unreachable">{(requests.error as Error).message}</DeferredNotice>
        ) : (requests.data?.items.length ?? 0) === 0 ? (
          <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
            No service requests yet.
          </p>
        ) : (
          <div className="bg-white border border-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                <tr>
                  <th className="text-left px-4 py-2">Request #</th>
                  <th className="text-left px-4 py-2">Service</th>
                  <th className="text-left px-4 py-2">Agency</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {requests.data?.items.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono text-xs">{r.requestNumber}</td>
                    <td className="px-4 py-2 text-xs text-gray-700">{r.serviceCode}</td>
                    <td className="px-4 py-2 text-xs text-gray-700">{r.agency}</td>
                    <td className="px-4 py-2"><StatusBadge status={r.status} /></td>
                    <td className="px-4 py-2 text-xs text-muted">{new Date(r.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
